import { PageSection } from 'components';
import { CarouselConfig } from 'containers/pages/for-governments/components/organizations/Organizations';
import { PaginationOutput } from 'kl-b2c-ui-kit';
import React, { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import Slider, { Settings } from 'react-slick';
import { getSpeakerApiService } from 'services/api.factory';
import { GetSpeakers, Lang, Speaker } from 'types';
import {
    Container,
    DescriptionWrapper,
    Disclaimer,
    DisclaimerDescription,
    DisclaimerItem,
    DisclaimerSubtitle,
    DisclaimerTitle,
    Hexa,
    Slide,
    SlideImage,
    SliderStyler,
    SlideSubtitle,
    SlideTitle,
    SpeakersButton,
    Subtitle,
    Title,
    Wrapper,
    PersonalDataDisclaimer,
} from './styled';
import { langMapper } from 'utils';
import { DotsContainer, DotsList } from '../reviews/styled';

const sectionConfig = {
    CWrap: {
        css: {
            paddingTop: 270,
            paddingBottom: 250,
            background: '#3DE8CA',
            backgroundImage:
                'radial-gradient(ellipse farthest-corner at 92% 95%, #4DFF88, transparent 40%)',

            '@media(max-width: 1279px)': {
                backgroundImage: 'none',
            },

            '@media(max-width: 1023px)': {
                paddingTop: 100,
            },
        },
    },
    ContentArea: {
        css: {
            display: 'block',
            position: 'relative',
            zIndex: 2,
        },
    },
};

const DEFAULT_SLIDES_COUNT = 3;

interface SpeakersProps {
    apiParams: GetSpeakers;
}

export const Speakers: FC<SpeakersProps> = ({ apiParams }) => {
    const { t, i18n } = useTranslation('pages/for-businesses');
    const speakersApi = getSpeakerApiService();
    const [speakers, setSpeakers] = useState<Speaker[]>();
    const navigate = useNavigate();
    const [carouselConfig, setCarouselConfig] = useState<CarouselConfig>({
        slidesToShow: 3,
        arrows: false,
        dots: true,
    });

    const CAROUSEL_SETTINGS: Settings = {
        slidesToScroll: 3,
        infinite: false,
        appendDots: (dots) => (
            <DotsContainer>
                <DotsList
                    styles={{
                        '.slick-active': {
                            backgroundColor: '#fff',
                        },
                    }}>
                    {dots}
                </DotsList>
            </DotsContainer>
        ),
        ...carouselConfig,
        responsive: [
            {
                breakpoint: 1023,
                settings: {
                    slidesToShow: 1,
                    slidesToScroll: 1,
                    dots: true,
                },
            },
        ],
    };

    useEffect(() => {
        speakersApi
            .getSpeakers({
                ...apiParams,
            })
            .then((speakers: PaginationOutput<Speaker>) => {
                if (speakers.items && speakers.items.length) {
                    setSpeakers(speakers?.items);

                    if (speakers?.items?.length <= DEFAULT_SLIDES_COUNT) {
                        setCarouselConfig({
                            dots: false,
                            arrows: false,
                            slidesToShow: speakers.items.length,
                        });
                    }
                }
            })
            .catch((e) => console.error(e));
    }, []);

    return (
        <Wrapper>
            <Hexa
                image={
                    process.env.PUBLIC_URL +
                    '/assets/images/bussiness/hexa1.png'
                }
            />
            <PageSection config={sectionConfig}>
                {speakers && speakers.length > 0 && (
                    <Container>
                        <DescriptionWrapper>
                            <Title>{t('speakers-title')}</Title>
                            <Subtitle>{t('speakers-subtitle')}</Subtitle>
                            <SpeakersButton
                                styles={{
                                    fontSize: '21px',
                                    lineHeight: '24px',
                                }}
                                onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    return navigate('/courses');
                                }}
                                variant={'contained'}
                                color={'primary-contrast'}
                                size={'large'}>
                                {t('choose-course')}
                            </SpeakersButton>
                        </DescriptionWrapper>

                        <SliderStyler alignTop={speakers.length < 3}>
                            <Slider
                                {...CAROUSEL_SETTINGS}
                                swipe={speakers.length > 3}>
                                {speakers.map((speaker) => (
                                    <Slide key={speaker.id}>
                                        <SlideImage
                                            image={speaker.image?.fileLink}
                                        />
                                        <SlideTitle>
                                            {speaker.firstName}{' '}
                                            {speaker.lastName}
                                        </SlideTitle>
                                        <SlideSubtitle>
                                            {speaker.position}
                                        </SlideSubtitle>
                                    </Slide>
                                ))}
                            </Slider>
                            {langMapper[i18n.language] === Lang.Ru && (
                                <PersonalDataDisclaimer>
                                    {t('personal-data')}
                                </PersonalDataDisclaimer>
                            )}
                        </SliderStyler>
                    </Container>
                )}

                <Disclaimer>
                    <DisclaimerItem>
                        <DisclaimerTitle>
                            {t('disclaimer-title-1')}
                        </DisclaimerTitle>
                        <DisclaimerSubtitle>
                            {t('disclaimer-subtitle-1')}

                            <DisclaimerDescription>
                                {t('disclaimer-description-1')}
                            </DisclaimerDescription>
                        </DisclaimerSubtitle>
                    </DisclaimerItem>
                    <DisclaimerItem>
                        <DisclaimerTitle>
                            {t('disclaimer-title-2')}
                        </DisclaimerTitle>
                        <DisclaimerSubtitle>
                            {t('disclaimer-subtitle-2')}

                            <DisclaimerDescription>
                                {t('disclaimer-description-2')}
                            </DisclaimerDescription>
                        </DisclaimerSubtitle>
                    </DisclaimerItem>
                    <DisclaimerItem>
                        <DisclaimerTitle>
                            {t('disclaimer-title-3')}
                        </DisclaimerTitle>
                        <DisclaimerSubtitle>
                            {t('disclaimer-subtitle-3')}

                            <DisclaimerDescription>
                                {t('disclaimer-description-3')}
                            </DisclaimerDescription>
                        </DisclaimerSubtitle>
                    </DisclaimerItem>
                </Disclaimer>
            </PageSection>
        </Wrapper>
    );
};
